/**
 * Page entry logic: choose between the editor (desktop or compact) and the
 * "Best on desktop" fallback, then boot the engine and hand the result to React.
 *
 * The fallback never boots the engine (no three.js download). A page opened
 * narrow boots the editor the first time the window becomes wide enough. A
 * running editor that gets narrowed shows the fallback on top and pauses
 * rendering (architecture §10.2).
 */
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import type { Root } from "react-dom/client";
import { AssetsModule } from "../assets/AssetsModule";
import { CommandsModule } from "../commands/CommandsModule";
import { CoreModule } from "../core/CoreModule";
import type { Engine } from "../core/Engine";
import { BootMonitor } from "../editor/boot/BootMonitor";
import { editorServicesFrom } from "../editor/EditorContext";
import type { EditorServices } from "../editor/EditorContext";
import { EditorModule } from "../editor/EditorModule";
import { ThemeModule } from "../editor/theme/ThemeModule";
import type { LevelService } from "../editor/levels/LevelService";
import type { EditorSettings } from "../editor/settings/EditorSettings";
import { RendererModule } from "../render/RendererModule";
import { WorldModule } from "../world/WorldModule";
import { redirectedUrl } from "../editor/router/route";
import { Router } from "../editor/router/Router";
import { App } from "./App";
import { browserHistory } from "./browserHistory";
import { readTourPreference, shouldOfferTour, writeTourPreference } from "./tourPreference";
import type { AppEditor } from "./App";
import { EDITOR_CONFIG } from "./config";
import { createEditorEngine, EDITOR_MODULES, SETTINGS_STORAGE_KEY } from "./createEditorEngine";
import { debugFacadeFor, installDebugFacade } from "./debugFacade";
import { detectMode, layoutForMode, readModeInput, readShellProfile } from "./environment";
import type { EditorLayout } from "./environment";
import { VersionedStore } from "../core/store";
import type { ReadableStore } from "../core/store";
import { followLevelTitle } from "./pageTitle";

export function start(win: Window, doc: Document): void {
  // A deep link that went through GitHub Pages' 404 shim arrives as
  // `?redirect=…`: restore it first, so `?debug`, `?rhi=` and the Router all
  // see the address the visitor actually opened (SPEC §9).
  const original = redirectedUrl(win.location.search);
  if (original) win.history.replaceState(null, "", original);

  let editor: Engine | null = null;
  // Which layout the running editor shows. A store, so a window that crosses
  // the phone boundary switches layouts live, with the world untouched.
  let layout: VersionedStore<EditorLayout> | null = null;

  const applyMode = () => {
    const mode = detectMode(readModeInput(win));
    doc.body.dataset.mode = mode;

    if (mode !== "fallback") {
      const next = layoutForMode(mode);
      if (!editor) {
        editor = createEditorEngine(win, doc);
        layout = new VersionedStore(editor.kernel.scheduler, next);
        void bootEditor(win, doc, editor, layout);
      } else if (layout && layout.getSnapshot() !== next) {
        layout.publish(next);
      }
    }
    if (editor?.has(CoreModule)) {
      editor.get(CoreModule).frameLoop.setPaused(mode === "fallback");
    }
  };

  applyMode();
  win.addEventListener("resize", applyMode);
}

async function bootEditor(
  win: Window,
  doc: Document,
  engine: Engine,
  layout: ReadableStore<EditorLayout>,
): Promise<void> {
  const { clock, scheduler, log } = engine.kernel;
  const monitor = new BootMonitor(scheduler);
  monitor.attach(engine.events);

  const reducedMotion = win.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const renderApp = mountApp(doc, {
    log,
    monitor,
    clock,
    profile: readShellProfile(doc, import.meta.env.BASE_URL),
    layout,
    bootStartedMs: clock.now(),
    minBootDisplayMs: reducedMotion ? 0 : EDITOR_CONFIG.minBootDisplayMs,
  });
  renderApp(null);
  installDebugFacade(win, debugFacadeFor(engine, monitor));

  try {
    await engine.boot(EDITOR_MODULES);
  } catch (thrown) {
    monitor.markFailed(thrown instanceof Error ? thrown.message : String(thrown));
    return;
  }

  const services = editorServicesFrom({
    core: engine.get(CoreModule),
    commands: engine.get(CommandsModule),
    assets: engine.get(AssetsModule),
    world: engine.get(WorldModule),
    editor: engine.get(EditorModule),
    renderer: engine.get(RendererModule),
    theme: engine.get(ThemeModule),
    base: import.meta.env.BASE_URL,
  });

  // The final boot step: the Router opens the level the address names (the
  // startup level at the site root) with the same `level.open` the Content
  // Browser uses. A failure is logged by the bus, and the editor still opens
  // (architecture §2.2).
  const stopTracking = trackStartupStream(services.levels, monitor);
  const { deepLinked } = await routerFor(win, engine).start();
  stopTracking();
  persistSettings(win, services.settings);
  rememberTourCompletion(win, services);
  followLevelTitle(
    win.document,
    services.world,
    services.content.index.levels,
    services.content.seed.profile.name,
  );
  flushAutosaveWhenHidden(win, engine);

  monitor.markReady();
  // When the editor became usable, on the page's own timeline: the perf smoke
  // counts what boot fetched before it (background streams start after).
  win.performance.mark("kn:ready");
  const offerTour = shouldOfferTour({ preference: readTourPreference(win), deepLinked });
  renderApp({
    services,
    tourOffer: offerTour
      ? {
          onClose: (dontShowAgain) => {
            if (dontShowAgain) writeTourPreference(win, "dismissed");
          },
        }
      : null,
  });
  engine.get(CoreModule).frameLoop.invalidate("editor ready");
}

/**
 * Saves a pending edit before the page goes away or out of sight: autosave
 * waits a moment after edits, and a closed (or discarded background) tab
 * mustn't lose them.
 */
function flushAutosaveWhenHidden(win: Window, engine: Engine): void {
  const flush = () => {
    engine.get(EditorModule).autosave?.flush();
  };
  win.addEventListener("pagehide", flush);
  win.document.addEventListener("visibilitychange", () => {
    if (win.document.visibilityState === "hidden") flush();
  });
}

/** A tour that reaches its end is remembered, so the offer stops appearing (A-25). */
function rememberTourCompletion(win: Window, services: EditorServices): void {
  services.tour.subscribe(() => {
    if (services.tour.getSnapshot().completed) writeTourPreference(win, "completed");
  });
}

function routerFor(win: Window, engine: Engine): Router {
  const editor = engine.get(EditorModule);
  return new Router({
    history: browserHistory(win),
    base: import.meta.env.BASE_URL,
    startupLevel: EDITOR_CONFIG.startupLevel,
    bus: engine.get(CommandsModule).bus,
    world: engine.get(WorldModule).world,
    levels: editor.levels,
    log: engine.log,
  });
}

/** Mirrors the startup level's real byte stream into the boot progress bar. */
function trackStartupStream(levels: LevelService, monitor: BootMonitor): () => void {
  let started = false;
  return levels.progress.subscribe(() => {
    const progress = levels.progress.getSnapshot();
    if (progress.phase !== "loading") return;
    if (!started) {
      monitor.beginStreaming(progress.assetPath, progress.bytesTotal);
      started = true;
    }
    monitor.streamProgress(progress.bytesLoaded, progress.bytesTotal);
  });
}

/** Saves persisted settings whenever they change. Storage may be unavailable (privacy modes). */
function persistSettings(win: Window, settings: EditorSettings): void {
  settings.subscribe(() => {
    try {
      win.localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(settings.persistedValues()));
    } catch {
      // Settings simply won't persist this session.
    }
  });
}

type StaticAppProps = Omit<Parameters<typeof App>[0], "editor">;

/** Creates the React root once; returns a function that re-renders with editor services. */
function mountApp(doc: Document, props: StaticAppProps): (editor: AppEditor | null) => void {
  const container = doc.getElementById("root");
  if (!container) throw new Error("index.html is missing #root");
  const root: Root = createRoot(container);

  return (editor) => {
    root.render(
      <StrictMode>
        <App {...props} editor={editor} />
      </StrictMode>,
    );
  };
}
